import { useState, useEffect } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useAuth, API } from "@/App";
import axios from "axios";
import {
  Leaf,
  Search,
  User,
  Star,
  Clock,
  Filter,
  LogOut,
  Shield,
  MapPin,
  Briefcase,
  Send,
  DollarSign,
  X,
  CheckCircle,
  Crown,
  Calendar
} from "lucide-react";
import { toast } from "sonner";

const CoachesPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, token, logout } = useAuth();
  const [coaches, setCoaches] = useState([]);
  const [filteredCoaches, setFilteredCoaches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [genderFilter, setGenderFilter] = useState("");
  const [selectedCoach, setSelectedCoach] = useState(null);
  const [connectDialogOpen, setConnectDialogOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [sendingMessage, setSendingMessage] = useState(false);
  const [messageSent, setMessageSent] = useState(false);

  useEffect(() => {
    fetchCoaches();
  }, []);

  // Handle deep linking from dashboard to connect with a specific coach
  useEffect(() => {
    if (location.state?.connectToCoachId && coaches.length > 0) {
      const coach = coaches.find(c => c.id === location.state.connectToCoachId);
      if (coach) {
        openConnectDialog(coach);
        // Clear the state so it doesn't re-trigger
        window.history.replaceState({}, document.title);
      }
    }
  }, [location.state, coaches]);

  useEffect(() => {
    filterCoaches();
  }, [searchTerm, categoryFilter, genderFilter, coaches]);

  const fetchCoaches = async () => {
    try {
      const [coachesRes, matchesRes] = await Promise.all([
        axios.get(`${API}/coaches`, token ? { headers: { Authorization: `Bearer ${token}` } } : {}),
        token
          ? axios.get(`${API}/matches`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] }))
          : Promise.resolve({ data: [] })
      ]);

      const allCoaches = coachesRes.data;
      const matches = matchesRes.data || [];

      if (matches.length > 0) {
        // Merge match_reason and match_score into the full coach list
        const matchMap = {};
        matches.forEach(m => { matchMap[m.id] = m; });

        const matchedIds = new Set(matches.map(m => m.id));
        const matchedCoaches = matches; // already ordered by Claude
        const remainingCoaches = allCoaches
          .filter(c => !matchedIds.has(c.id))
          .map(c => ({ ...c, match_reason: null, match_score: null }));

        const merged = [...matchedCoaches, ...remainingCoaches];
        setCoaches(merged);
        setFilteredCoaches(merged);
      } else {
        setCoaches(allCoaches);
        setFilteredCoaches(allCoaches);
      }
    } catch (error) {
      console.error("Error fetching coaches:", error);
      toast.error("Failed to load coaches");
    } finally {
      setLoading(false);
    }
  };

  const filterCoaches = () => {
    let filtered = [...coaches];

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (coach) =>
          coach.first_name.toLowerCase().includes(term) ||
          coach.last_name.toLowerCase().includes(term) ||
          coach.bio?.toLowerCase().includes(term) ||
          coach.specializations?.some((s) => s.toLowerCase().includes(term))
      );
    }

    if (categoryFilter) {
      filtered = filtered.filter((coach) =>
        coach.specializations.includes(categoryFilter)
      );
    }

    if (genderFilter) {
      filtered = filtered.filter((coach) => coach.gender === genderFilter);
    }

    setFilteredCoaches(filtered);
  };

  const handleLogout = () => {
    logout();
    navigate("/");
    toast.success("Logged out successfully");
  };

  const openConnectDialog = (coach) => {
    setSelectedCoach(coach);
    setMessage("");
    setMessageSent(false);
    setConnectDialogOpen(true);
  };

  const handleSendMessage = async () => {
    if (!message.trim()) {
      toast.error("Please enter a message");
      return;
    }

    setSendingMessage(true);
    try {
      await axios.post(
        `${API}/messages`,
        { coach_id: selectedCoach.id, message: message },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setMessageSent(true);
      toast.success("Message sent! The coach will respond with their contact information.");
    } catch (error) {
      toast.error("Failed to send message. Please try again.");
    } finally {
      setSendingMessage(false);
    }
  };

  const CATEGORIES = [
    "Leadership Development",
    "Church Planting & Launch",
    "Pastoral Care & Burnout Recovery",
    "Sabbatical Planning & Re-entry",
    "Succession Planning",
    "Staff & Team Development",
    "Preaching & Communication",
    "Church Revitalization",
    "Personal Spiritual Formation",
    "Marriage & Family",
    "Executive & Organizational Leadership",
    "Conflict Resolution",
    "Bi-vocational Ministry",
    "Multicultural Ministry"
  ];

  return (
    <div className="min-h-screen bg-[#FAFAF9]">
      {/* Header */}
      <header className="glass-header sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <Leaf className="w-8 h-8 text-[#7C9A92]" />
            <span className="font-serif text-2xl font-medium text-[#2C332B]">CoachBridge</span>
          </Link>

          <nav className="hidden md:flex items-center gap-6">
            <Link to="/dashboard" className="text-[#78716C] hover:text-[#2C332B] font-medium">Dashboard</Link>
            <Link to="/coaches" className="text-[#7C9A92] font-medium">Coaches</Link>
            {user?.is_admin && (
              <Link to="/admin" className="text-[#78716C] hover:text-[#2C332B] font-medium flex items-center gap-1">
                <Shield className="w-4 h-4" />
                Admin
              </Link>
            )}
          </nav>

          <div className="flex items-center gap-4">
            <span className="hidden md:block text-[#78716C]">
              Welcome, {user?.first_name}
            </span>
            <Button
              variant="ghost"
              onClick={handleLogout}
              className="text-[#78716C] hover:text-[#2C332B]"
              data-testid="logout-btn"
            >
              <LogOut className="w-5 h-5" />
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-12">
        {/* Page Title */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <h1 className="font-serif text-4xl md:text-5xl font-light text-[#2C332B] mb-4">
            {token && coaches.some(c => c.match_reason) ? "Your Matches" : "Browse Coaches"}
          </h1>
          <p className="text-lg text-[#78716C]">
            {token && coaches.some(c => c.match_reason)
              ? "CoachBridge has reviewed your profile and selected these coaches for you."
              : "Find the perfect coach to guide your growth journey"}
          </p>
        </motion.div>

        {/* Filters */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-2xl p-6 border border-stone-100 shadow-sm mb-8"
        >
          <div className="flex flex-col md:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#78716C]" />
              <Input
                placeholder="Search coaches by name or specialty..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-12 h-12 rounded-xl border-stone-200 bg-stone-50/50"
                data-testid="search-coaches-input"
              />
            </div>

            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger className="w-full md:w-[200px] h-12 rounded-xl" data-testid="category-filter">
                <Filter className="w-4 h-4 mr-2 text-[#78716C]" />
                <SelectValue placeholder="Category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {CATEGORIES.map((cat) => (
                  <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={genderFilter} onValueChange={setGenderFilter}>
              <SelectTrigger className="w-full md:w-[150px] h-12 rounded-xl" data-testid="gender-filter">
                <User className="w-4 h-4 mr-2 text-[#78716C]" />
                <SelectValue placeholder="Gender" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="Male">Male</SelectItem>
                <SelectItem value="Female">Female</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </motion.div>

        {/* Results Count */}
        <p className="text-[#78716C] mb-6">
          Showing {filteredCoaches.length} coach{filteredCoaches.length !== 1 ? "es" : ""}
        </p>

        {/* Coaches Grid */}
        {loading ? (
          <div className="text-center py-20">
            <div className="animate-pulse text-[#7C9A92] text-xl">Loading coaches...</div>
          </div>
        ) : filteredCoaches.length > 0 ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredCoaches.map((coach, index) => (
              <motion.div
                key={coach.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
              >
                <Card className="coach-card h-full overflow-hidden border-stone-100 hover:border-[#7C9A92]/30">
                  <CardContent className="p-0">
                    {/* Coach Image */}
                    <div className="h-56 bg-[#F5F1E8] relative">
                      {coach.photo_url ? (
                        <img
                          src={coach.photo_url}
                          alt={`${coach.first_name} ${coach.last_name}`}
                          className="w-full h-full object-cover object-center"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <User className="w-20 h-20 text-[#7C9A92]/30" />
                        </div>
                      )}
                      {coach.match_score && (
                        <div className="absolute top-4 right-4 match-badge">
                          {coach.match_score}% Match
                        </div>
                      )}
                    </div>

                    {/* Coach Info */}
                    <div className="p-6">
                      <div className="flex items-start justify-between mb-1">
                        <h3 className="font-serif text-xl font-medium text-[#2C332B]">
                          {coach.first_name} {coach.last_name}
                        </h3>
                        {coach.is_preferred && (
                          <Badge className="bg-gradient-to-r from-[#E09F7D] to-[#7C9A92] text-white text-xs flex items-center gap-1">
                            <Crown className="w-3 h-3" />
                            Preferred
                          </Badge>
                        )}
                      </div>
                      
                      <div className="flex items-center gap-4 text-sm text-[#78716C] mb-4">
                        <span className="flex items-center gap-1">
                          <Clock className="w-4 h-4" />
                          {coach.experience_years} years
                        </span>
                        <span className="flex items-center gap-1">
                          <Briefcase className="w-4 h-4" />
                          {coach.availability}
                        </span>
                      </div>

                      <p className="text-[#78716C] text-sm mb-4 line-clamp-2">
                        {coach.bio}
                      </p>

                      {coach.match_reason && (
                        <div className="mb-4 p-3 rounded-xl bg-[#7C9A92]/8 border border-[#7C9A92]/20">
                          <p className="text-xs font-medium text-[#7C9A92] mb-1">Why CoachBridge matched you</p>
                          <p className="text-xs text-[#2C332B] leading-relaxed">{coach.match_reason}</p>
                        </div>
                      )}

                      <div className="flex flex-wrap gap-2 mb-4">
                        {coach.specializations.slice(0, 3).map((spec) => (
                          <Badge
                            key={spec}
                            variant="secondary"
                            className="bg-[#F5F1E8] text-[#2C332B] text-xs"
                          >
                            {spec}
                          </Badge>
                        ))}
                        {coach.specializations.length > 3 && (
                          <Badge variant="secondary" className="bg-stone-100 text-[#78716C] text-xs">
                            +{coach.specializations.length - 3}
                          </Badge>
                        )}
                      </div>

                      <div className="flex items-center justify-between pt-4 border-t border-stone-100">
                        <span className="text-lg font-semibold text-[#2C332B]">
                          ${coach.hourly_rate}/hr
                        </span>
                        <Button
                          onClick={() => openConnectDialog(coach)}
                          className="rounded-full bg-[#7C9A92] hover:bg-[#6B8A82] text-white px-6"
                          data-testid={`connect-coach-${coach.id}`}
                        >
                          Connect
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        ) : (
          <div className="text-center py-20">
            <User className="w-16 h-16 text-stone-300 mx-auto mb-4" />
            <h3 className="font-serif text-2xl text-[#2C332B] mb-2">No coaches found</h3>
            <p className="text-[#78716C]">
              {coaches.length === 0
                ? "No coaches have been added yet. Check back soon!"
                : "Try adjusting your filters to see more results."}
            </p>
          </div>
        )}

        {/* Connect Dialog */}
        <Dialog open={connectDialogOpen} onOpenChange={setConnectDialogOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-serif text-2xl">
                Connect with {selectedCoach?.first_name}
              </DialogTitle>
            </DialogHeader>
            
            {selectedCoach && !messageSent && (
              <div className="space-y-6 mt-4">
                {/* Coach Summary */}
                <div className="flex items-start gap-4 p-4 bg-[#F5F1E8] rounded-xl">
                  <div className="w-16 h-16 rounded-full bg-white overflow-hidden flex-shrink-0">
                    {selectedCoach.photo_url ? (
                      <img 
                        src={selectedCoach.photo_url} 
                        alt={selectedCoach.first_name}
                        className="w-full h-full object-cover object-center"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <User className="w-8 h-8 text-[#7C9A92]" />
                      </div>
                    )}
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-[#2C332B]">
                      {selectedCoach.first_name} {selectedCoach.last_name}
                    </h3>
                    <p className="text-sm text-[#78716C] mt-1 line-clamp-2">
                      {selectedCoach.bio}
                    </p>
                    <div className="flex items-center gap-4 mt-2">
                      <span className="flex items-center gap-1 text-sm text-[#7C9A92]">
                        <Clock className="w-4 h-4" />
                        {selectedCoach.experience_years} years exp.
                      </span>
                      <span className="flex items-center gap-1 text-sm text-[#7C9A92]">
                        <DollarSign className="w-4 h-4" />
                        ${selectedCoach.hourly_rate}/hr
                      </span>
                    </div>
                  </div>
                </div>

                {/* Free Consultation Notice */}
                <div className="p-4 bg-[#7C9A92]/10 rounded-xl border border-[#7C9A92]/20">
                  <p className="text-sm text-[#2C332B]">
                    <strong>Free 15-Minute Consultation:</strong> All CoachBridge coaches offer a complimentary 15-minute phone or video consultation to ensure a good match before any paid sessions.
                  </p>
                  {selectedCoach.booking_link && (
                    <a 
                      href={selectedCoach.booking_link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 mt-3 px-4 py-2 bg-[#7C9A92] text-white rounded-full text-sm font-medium hover:bg-[#6B8A82] transition-colors"
                      data-testid="schedule-consultation-btn"
                    >
                      <Calendar className="w-4 h-4" />
                      Schedule Free Consultation
                    </a>
                  )}
                </div>

                {/* Message Form */}
                <div className="space-y-3">
                  <Label className="text-[#2C332B] font-medium">
                    Introduce yourself and share what you're looking for in a coach:
                  </Label>
                  <Textarea
                    placeholder="Hi, I'm a pastor at... I'm looking for coaching in... I'd love to schedule a free consultation to see if we're a good fit."
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    className="min-h-[120px] rounded-xl border-stone-200"
                    data-testid="connect-message"
                  />
                </div>

                <p className="text-xs text-[#78716C]">
                  Your message will be sent to the coach along with your name and email. The coach will respond directly with their contact information to schedule your free consultation.
                </p>

                <div className="flex gap-3">
                  <Button
                    variant="outline"
                    onClick={() => setConnectDialogOpen(false)}
                    className="flex-1 rounded-full"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleSendMessage}
                    disabled={sendingMessage}
                    className="flex-1 rounded-full bg-[#7C9A92] hover:bg-[#6B8A82]"
                    data-testid="send-message-btn"
                  >
                    {sendingMessage ? "Sending..." : "Send Message"}
                    <Send className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </div>
            )}

            {messageSent && (
              <div className="text-center py-8">
                <div className="w-16 h-16 rounded-full bg-[#7C9A92]/10 flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="w-8 h-8 text-[#7C9A92]" />
                </div>
                <h3 className="font-serif text-xl text-[#2C332B] mb-2">Message Sent!</h3>
                <p className="text-[#78716C] mb-6">
                  {selectedCoach?.first_name} will receive your message and respond with their contact information to schedule your free 15-minute consultation.
                </p>
                <Button
                  onClick={() => setConnectDialogOpen(false)}
                  className="rounded-full bg-[#7C9A92] hover:bg-[#6B8A82]"
                >
                  Done
                </Button>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
};

export default CoachesPage;
